import React from 'react';

const severityStyles = {
  error: {
    wrap: 'border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/30',
    dot: 'bg-red-500',
    title: 'text-red-900 dark:text-red-100',
    text: 'text-red-800/90 dark:text-red-200/90',
  },
  warning: {
    wrap: 'border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30',
    dot: 'bg-amber-500',
    title: 'text-amber-900 dark:text-amber-100',
    text: 'text-amber-800/90 dark:text-amber-200/90',
  },
  info: {
    wrap: 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50',
    dot: 'bg-blue-500',
    title: 'text-slate-900 dark:text-slate-100',
    text: 'text-slate-600 dark:text-slate-400',
  },
  success: {
    wrap: 'border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/30',
    dot: 'bg-emerald-500',
    title: 'text-emerald-900 dark:text-emerald-100',
    text: 'text-emerald-800/90 dark:text-emerald-200/90',
  },
};

function EligibilityRow({ item }) {
  const st = severityStyles[item.severity] || severityStyles.info;
  return (
    <li className={`rounded-xl border px-3 py-2.5 ${st.wrap}`}>
      <div className="flex gap-2.5">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${st.dot}`} aria-hidden />
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${st.title}`}>{item.title}</p>
          <p className={`text-xs mt-0.5 leading-relaxed ${st.text}`}>{item.message}</p>
          {item.need != null && item.have != null && item.short > 0 ? (
            <p className={`text-xs mt-1.5 font-medium tabular-nums ${st.text}`}>
              Need {item.need} · Have {item.have} · Short {item.short}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/**
 * Shows why a user can or cannot play free on an event.
 */
export default function FreePlayEligibilityPanel({
  eligibility,
  loading = false,
  compact = false,
  onConnectWallet,
  connectingWallet = false,
}) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-4">
        <div className="h-4 w-40 rounded bg-stone-200 dark:bg-stone-700 animate-pulse mb-3" />
        <div className="space-y-2">
          <div className="h-14 rounded-xl bg-stone-100 dark:bg-stone-800 animate-pulse" />
          <div className="h-14 rounded-xl bg-stone-100 dark:bg-stone-800 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!eligibility) return null;

  const { canEnter, items, entryReqs } = eligibility;
  const errors = items.filter((i) => i.severity === 'error' || i.severity === 'warning');
  const infos = items.filter((i) => i.severity === 'info');

  return (
    <div
      className={`rounded-2xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 ${
        compact ? 'p-3' : 'p-4'
      } space-y-3`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-stone-900 dark:text-white">Free play status</h3>
          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
            {canEnter
              ? 'You meet the requirements to place a free pick on this event.'
              : 'Complete the steps below before you can predict.'}
          </p>
        </div>
        {canEnter ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 shrink-0">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
            Ready to play
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 shrink-0">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden />
            Action needed
          </span>
        )}
      </div>

      {(entryReqs?.minFreeTickets > 0 || entryReqs?.minGoldenTickets > 0) && (
        <div className="flex flex-wrap gap-2 text-[11px]">
          {entryReqs.minFreeTickets > 0 ? (
            <span className="px-2 py-1 rounded-md bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 font-medium">
              Minimum free ticket{entryReqs.minFreeTickets === 1 ? '' : 's'} required: {entryReqs.minFreeTickets}
            </span>
          ) : null}
          {entryReqs.minGoldenTickets > 0 ? (
            <span className="px-2 py-1 rounded-md bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-200 font-medium">
              Minimum golden ticket{entryReqs.minGoldenTickets === 1 ? '' : 's'} required: {entryReqs.minGoldenTickets}
            </span>
          ) : null}
        </div>
      )}

      {errors.length > 0 || infos.length > 0 ? (
        <ul className="space-y-2">
          {errors.map((item) => (
            <EligibilityRow key={item.code} item={item} />
          ))}
          {infos.map((item) => (
            <EligibilityRow key={item.code} item={item} />
          ))}
        </ul>
      ) : canEnter ? (
        <EligibilityRow
          item={{
            severity: 'success',
            title: 'All set',
            message: 'Choose an outcome below to stake your tickets on a free pick.',
          }}
        />
      ) : null}

      {items.some((i) => i.code === 'wallet_nft') && onConnectWallet ? (
        <button
          type="button"
          onClick={onConnectWallet}
          disabled={connectingWallet}
          className="w-full sm:w-auto px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {connectingWallet ? 'Connecting…' : 'Connect wallet to verify NFTs'}
        </button>
      ) : null}
    </div>
  );
}
